import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SITE } from "@/lib/site";

const links = [
  { to: "/#expertise", label: "Expertise" },
  { to: "/#services", label: "Services" },
  { to: "/#testimonials", label: "Voices" },
  { to: "/#blog", label: "Insights" },
  { to: "/#contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const handleAnchor = (e, to) => {
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (loc.pathname === "/") {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <div className="h-9 w-9 rounded-md bg-gradient-to-br from-blue-500 to-cyan-400 grid place-items-center font-display font-extrabold text-black text-sm">
            {SITE.initials}
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-white text-base tracking-tight">{SITE.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
              {SITE.domain}
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            l.to.startsWith("/#") ? (
              <a
                key={l.to}
                href={l.to.replace("/", "")}
                onClick={(e) => handleAnchor(e, l.to)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm text-slate-300 hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm text-slate-300 hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            )
          ))}
        </nav>

        <div className="hidden md:block">
          <a
            href="#contact"
            onClick={(e) => handleAnchor(e, "/#contact")}
            data-testid="navbar-book-cta"
            className="inline-flex items-center px-5 py-2.5 bg-white text-black text-sm font-medium rounded-full hover:bg-slate-200 transition-colors"
          >
            Book a Call
          </a>
        </div>

        <button
          className="md:hidden text-white p-2"
          aria-label="Menu"
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden glass border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.to}
                  href={l.to.startsWith("/#") ? l.to.replace("/", "") : l.to}
                  onClick={(e) => handleAnchor(e, l.to)}
                  className="text-base text-slate-200 py-2 border-b border-white/5"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => handleAnchor(e, "/#contact")}
                className="mt-2 text-center px-5 py-3 bg-white text-black font-medium rounded-full"
              >
                Book a Call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
